import { useContext } from 'react'

import dayjs from 'dayjs'

import {
  useLinkageApiKeyDeleteApi,
  useLinkageApiKeyListApi
} from '@/common/api/modules/linkageApiKey'
import { linkageApiKeyEditPath, linkageApiKeyNewPath } from '@/common/app/path'
import { useShowCopiedToast, useShowCopyFailedToast, useShowDeletedToast } from '@/common/app/toast'
import { LinkageApiKey } from '@/common/model/linkageApiKey'
import { copyTextToClipboard } from '@/common/util/clipboard'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { DeleteDialogContext } from '@/components/container/modal/dialog/delete/DeleteDialogProvider'

const formatDateTime = (value: string | null) =>
  value ? dayjs(value).format('YYYY/MM/DD HH:mm') : null

const LinkageApiKeyListPage = () => {
  const showDeleted = useShowDeletedToast()
  const showCopied = useShowCopiedToast()
  const showCopyFailed = useShowCopyFailedToast()
  const { showModal, hide } = useContext(DeleteDialogContext)
  const { data, isLoading, mutate } = useLinkageApiKeyListApi()
  const { trigger: deleteLinkageApiKey } = useLinkageApiKeyDeleteApi({
    onSuccess: () => {
      hide()
      mutate()
    }
  })

  const onClickDelete = (linkageApiKey: LinkageApiKey) => {
    showModal({
      name: linkageApiKey.purpose,
      onClickDelete: () =>
        deleteLinkageApiKey({ linkage_api_key_id: linkageApiKey.id }).then(() =>
          showDeleted(linkageApiKey.purpose)
        )
    })
  }

  const onClickCopy = (linkageApiKey: LinkageApiKey) => {
    copyTextToClipboard(linkageApiKey.api_key).then(
      () => showCopied('APIキー'),
      () => showCopyFailed('APIキー')
    )
  }

  const linkageApiKeys = data ?? []

  return (
    <Main type={MainLayoutType.MEDIUM} bottomSpace>
      <PageTitle
        title={'連携APIキー管理'}
        description='関連プロダクトへ連携するAPIの認証キーを管理します'
        rightNode={
          <Button link={linkageApiKeyNewPath()} size='large' color={ButtonColor.PRIMARY_BRAND}>
            新規APIキーを追加する
          </Button>
        }
      />
      <Content>
        {isLoading ? (
          <PageListLoading />
        ) : (
          <Area tableWrap noBorder>
            <Table>
              <thead>
                <TableRow head>
                  {['用途', 'APIキー', 'IP制限', '作成日時', '最終利用日時', '操作'].map(
                    (label) => (
                      <TableHeadItem key={label}>
                        <Text>{label}</Text>
                      </TableHeadItem>
                    )
                  )}
                </TableRow>
              </thead>
              <tbody>
                {linkageApiKeys.length === 0 ? (
                  <TableRow>
                    <TableRowItem>
                      <Text color={TextColor.SECONDARY}>連携APIキーがありません</Text>
                    </TableRowItem>
                  </TableRow>
                ) : (
                  linkageApiKeys.map((k) => (
                    <TableRow key={k.id}>
                      <TableRowItem>
                        <Text>{k.purpose}</Text>
                      </TableRowItem>
                      <TableRowItem>
                        <ItemAlign gapX={8} align='center'>
                          <Text>{k.api_key}</Text>
                          <Button color={ButtonColor.SECONDARY} onClick={() => onClickCopy(k)}>
                            コピー
                          </Button>
                        </ItemAlign>
                      </TableRowItem>
                      <TableRowItem>
                        {k.allowed_ips.length > 0 ? (
                          <Badge badgeType={BadgeType.SQUARE_PRIMARY}>
                            {`${k.allowed_ips.length}件`}
                          </Badge>
                        ) : (
                          <Badge badgeType={BadgeType.SQUARE_SECONDARY}>制限なし</Badge>
                        )}
                      </TableRowItem>
                      <TableRowItem>
                        <Text>{formatDateTime(k.created_at) ?? '-'}</Text>
                      </TableRowItem>
                      <TableRowItem>
                        {k.last_used_at ? (
                          <Text>{formatDateTime(k.last_used_at)}</Text>
                        ) : (
                          <Text color={TextColor.SECONDARY}>未使用</Text>
                        )}
                      </TableRowItem>
                      <TableRowItem>
                        <ItemAlign gapX={8} align='center'>
                          <Button link={linkageApiKeyEditPath(k.id)} color={ButtonColor.SECONDARY}>
                            編集する
                          </Button>
                          <Button
                            color={ButtonColor.SECONDARY}
                            square
                            onClick={() => onClickDelete(k)}
                          >
                            <Icon type={IconType.DELETE} color={IconColor.CRITICAL} line />
                          </Button>
                        </ItemAlign>
                      </TableRowItem>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
          </Area>
        )}
      </Content>
    </Main>
  )
}

export default LinkageApiKeyListPage
